import { useNavigation } from '@react-navigation/native'
import { useTheme } from 'contexts'
import React, { useEffect, useMemo, useRef } from 'react'
import { StyleSheet } from 'react-native'
import { v4 as uuidv4 } from 'uuid'

import { Box } from 'widgets/Box'
import { Image } from 'widgets/Image'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'
import { UpscoreBadge } from 'widgets/UpscoreBadge'

import { useGroup } from 'hooks/useGroup'
import { useLeaderboard } from 'hooks/useLeaderboard'
import { useSubscribed } from 'hooks/useSubscribed'
import { useUser } from 'hooks/useUser'

import { links } from 'utils/constants'

import { useFocusMode } from 'contexts/FocusModeContext'

import { Room } from 'types/chat'
import { UserType } from 'types/user'

import { Avatar } from '../Avatar'

type CoachItemProps = {
  displayedForUser?: UserType
  user: UserType
  isSquare?: boolean
  isMember?: boolean
  withLeaderboardPosition?: boolean
  isUpspace?: boolean
  room?: Room | null | undefined
  isFeedHeader?: boolean
  withUpscoreBadge?: boolean
}

export const CoachItem = ({
  displayedForUser,
  user,
  isSquare = false,
  isMember = false,
  withLeaderboardPosition = false,
  isUpspace = false,
  room,
  isFeedHeader = false,
  withUpscoreBadge = false,
}: CoachItemProps) => {
  const isDisplayedForUser = displayedForUser?.id === user?.id
  const theme = useTheme()
  const styles = StyleSheet.create({
    roundContainer: {
      borderColor: !isFeedHeader ? theme.colors.primary : undefined,
      borderWidth: !isFeedHeader ? 1 : undefined,
      borderRadius: theme.spacing.xl,
    },
    squareContainer: {
      borderRadius: 5,
    },
    squareImage: {
      width: !isFeedHeader ? 80 : 140,
      height: !isFeedHeader ? 80 : 140,
      borderRadius: 5,
    },
    leaderboardText: {
      marginLeft: 1,
    },
    leaderboardIndicatorContainer: {
      right: 0,
      top: -5,
    },
    unreadIndicator: {
      position: 'absolute',
      zIndex: 99,
      top: -4,
      right: -4,
      borderStyle: 'solid',
      borderWidth: 2,
      borderColor: 'white',
    },
    upscoreContainer: {
      position: 'absolute',
      top: 0,
      right: 0,
      zIndex: 2,
      paddingLeft: 2,
    },
  })

  const navigation = useNavigation<any>()
  const { data: trainer } = useUser({ userId: user.id })
  const { data: group } = useGroup({ userId: user.id })
  const firstName = trainer?.firstName ?? user.firstName ?? ''
  const lastName = trainer?.lastName ?? user.lastName ?? ''
  const title = group?.title ?? `${firstName} ${lastName}'s Group`
  const avatar = isMember
    ? trainer?.avatar ?? user.avatar
    : group?.cover ?? trainer?.avatar ?? user.avatar ?? { url: '', mimeType: '', _original: {} }

  const { data: leaderboard } = useLeaderboard({ trainerId: user.id })
  const leaderboardLength = useRef(leaderboard?.length)
  const leaderboardPosition = useRef(-1)
  const shouldShowLeaderboardPosition = useRef(false)

  useEffect(() => {
    if (withLeaderboardPosition) {
      shouldShowLeaderboardPosition.current =
        !!isDisplayedForUser ||
        !!(leaderboard !== undefined && leaderboardLength.current && leaderboardPosition.current)

      if (shouldShowLeaderboardPosition.current && displayedForUser?.id) {
        const leaderboardIndex = leaderboard?.findIndex(
          (entry) => entry.id === displayedForUser?.id,
        )
        leaderboardPosition.current =
          leaderboardIndex && leaderboardIndex > -1 ? leaderboardIndex : leaderboard?.length ?? 0
        leaderboardLength.current =
          // eslint-disable-next-line no-nested-ternary
          leaderboardIndex && leaderboardIndex > -1
            ? leaderboard?.length ?? 0
            : leaderboard
            ? leaderboard?.length + 1
            : 1
      }
    }
  }, [
    displayedForUser?.id,
    isDisplayedForUser,
    leaderboard,
    leaderboardLength,
    withLeaderboardPosition,
  ])

  const hasUnreadMessages: boolean = useMemo(() => {
    if (room && room?.meta && room.meta.newMessageCount && room.meta.newMessageCount > 0) {
      return true
    }
    return false
  }, [room])

  const focusMode = useFocusMode()
  const { isSubscribed } = useSubscribed({ programId: group?.id })

  const navigateToUpspace = () => {
    if (isSubscribed) {
      focusMode.setGroup(group)
      return
    }
    navigation.navigate(links.GROUP_STACK, {
      screen: links.GROUP_PAGE,
      params: { username: trainer?.username },
    })
  }

  const navigateToProfile = () => {
    navigation.navigate(links.PROFILE_STACK, {
      screen: links.MEMBER_PROFILE_PAGE,
      params: { username: trainer?.username },
    })
  }

  const navigateToUpscorePage = () => {
    navigation.navigate(links.UPSCORE_PAGE, {
      userId: user.id,
    })
  }

  const handlePress = () => {
    if (!isUpspace) {
      isMember ? navigateToProfile() : navigateToUpspace()
    } else if (room) {
      navigation.navigate(links.CHAT_ROOM, { roomId: room.id })
    }
  }

  return (
    <Box
      // eslint-disable-next-line no-nested-ternary
      width={!isSquare ? (!isFeedHeader ? 64 : 146) : !isFeedHeader ? 90 : 150}
      display='flex'
      flexDirection='column'
      align='center'
      justifyContent='flex-start'
      margin={{ horizontal: 'sm', bottom: isSquare ? 10 : undefined }}
      padding={{ top: isSquare ? 8 : undefined }}
      backgroundColor='white'
      testID={`CoachItem-${user.id}`}
    >
      <Pressable onPress={handlePress}>
        <Box
          margin='xs'
          padding='xxs'
          style={isSquare ? styles.squareContainer : styles.roundContainer}
        >
          {isSquare ? (
            <>
              {withLeaderboardPosition && (
                <>
                  {shouldShowLeaderboardPosition.current && (
                    <Box
                      testID='LeadboardIndicatorContainer'
                      backgroundColor={!isDisplayedForUser ? 'v2primary' : 'v2secondary'}
                      border={{ color: 'white', width: 2 }}
                      borderRadius='smd'
                      padding={{ vertical: 'xxs', horizontal: 'sm' }}
                      align='center'
                      position='absolute'
                      zIndex={2}
                      style={styles.leaderboardIndicatorContainer}
                    >
                      <Text
                        bold
                        fontSize='xs'
                        color='white'
                        align='center'
                        style={styles.leaderboardText}
                      >
                        {!isDisplayedForUser
                          ? `${leaderboardPosition.current + 1} / ${leaderboardLength.current}`
                          : 'CREATOR'}
                      </Text>
                    </Box>
                  )}
                  <Image
                    cacheKey={
                      group
                        ? // eslint-disable-next-line no-underscore-dangle
                          `${group?.id}-group-cover-${group?.cover?._original?.etag}-small`
                        : uuidv4()
                    }
                    imageWidth={160}
                    resizeMode='cover'
                    style={styles.squareImage}
                    source={{ uri: avatar?.url }}
                  />
                </>
              )}
              {!withLeaderboardPosition && isUpspace && (
                <Box backgroundColor='white' justifyContent='center' align='center'>
                  {hasUnreadMessages && (
                    <Box
                      testID='ChatIcon-UnreadIndicator'
                      backgroundColor='v2secondary'
                      borderRadius='md'
                      align='center'
                      width={15}
                      height={15}
                      style={styles.unreadIndicator}
                    />
                  )}
                  <Image
                    cacheKey={
                      group
                        ? // eslint-disable-next-line no-underscore-dangle
                          `${group?.id}-group-cover-${group?.cover?._original?.etag}-small`
                        : uuidv4()
                    }
                    imageWidth={160}
                    resizeMode='cover'
                    style={styles.squareImage}
                    source={{ uri: avatar?.url }}
                  />
                </Box>
              )}
              {!withLeaderboardPosition && !isUpspace && (
                <Image
                  cacheKey={
                    group
                      ? // eslint-disable-next-line no-underscore-dangle
                        `${group?.id}-group-cover-${group?.cover?._original?.etag}-small`
                      : uuidv4()
                  }
                  imageWidth={160}
                  resizeMode='cover'
                  style={styles.squareImage}
                  source={{ uri: avatar?.url }}
                />
              )}
            </>
          ) : (
            <Box>
              <Avatar
                width={!isFeedHeader ? 60 : 136}
                height={!isFeedHeader ? 60 : 136}
                image={avatar}
              />
              {withUpscoreBadge && (
                <Box testID='UpscoreContainer' style={styles.upscoreContainer}>
                  <UpscoreBadge
                    text={`${trainer?.upscore?.total ?? 0}`}
                    onPress={navigateToUpscorePage}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box>
          <Text bold align='center' fontSize='xs' color='v2black' numberOfLines={2}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {isSquare
              ? title
              : !isFeedHeader
              ? `${firstName}\n${lastName}`
              : `${firstName} ${lastName}`}
          </Text>
        </Box>
      </Pressable>
    </Box>
  )
}
